import {
  Calendar,
  Clock,
  FileText,
  Home,
  PhoneForwarded,
  Sparkles,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Real Estate Agencies — Never Miss a Buyer or Seller Call',
  seoDescription:
    'Capture every buyer inquiry, showing request, and seller callback — even when your agents are at showings. AI receptionist for real estate that works 24/7.',
  path: '/industries/real-estate',
  badge: 'Real Estate',
  headline: 'Capture every buyer inquiry — even when you\'re at a showing.',
  subheadline:
    'Your AI receptionist answers calls, provides listing details, books showing times, and transfers hot leads to your agents instantly — so no opportunity goes cold.',
  pains: [
    'Buyers call during showings, reach voicemail, and inquire on a competing listing instead.',
    'Agents waste time fielding repetitive calls about listing details and open home times.',
    'Weekend and after-hours inquiries sit unanswered until Monday, when interest has cooled.',
  ],
  features: [
    {
      icon: Home,
      title: 'Listing Q&A',
      desc: 'Upload your current listings. The agent answers price guides, bedroom counts, open home times, and property details accurately.',
    },
    {
      icon: Calendar,
      title: 'Showing request capture',
      desc: 'Captures buyer name, contact, property interest, and preferred showing time. Delivered to your agent\'s dashboard instantly.',
    },
    {
      icon: PhoneForwarded,
      title: 'Hot lead transfer',
      desc: 'Buyers ready to make an offer get transferred to your agent immediately, with caller context already spoken through.',
    },
    {
      icon: Sparkles,
      title: 'Answers from your knowledge base',
      desc: 'Agency fees, appraisal process, suburb market updates — answered from your content, not invented.',
    },
    {
      icon: Clock,
      title: '24/7 coverage',
      desc: 'Saturday open-house calls, Sunday evening inquiries, holiday callbacks — answered every time.',
    },
    {
      icon: FileText,
      title: 'Inquiry log & summaries',
      desc: 'Every call logged with an AI summary. Review buyer interest and follow-up priorities from one place.',
    },
  ],
  faqs: [
    {
      q: 'Can it provide price guides for specific listings?',
      a: 'Yes — include price guide ranges, property features, and open home schedules in your knowledge base. The agent answers accurately from that content.',
    },
    {
      q: 'What happens when a buyer wants to make an offer?',
      a: 'Define a hot-lead transfer rule. The agent recognises buying intent and connects the caller to your agent immediately.',
    },
    {
      q: 'Can it handle rental inquiries as well as sales?',
      a: 'Yes — configure separate knowledge base sections for rentals and sales, with different intake flows for each.',
    },
    {
      q: 'Does it work across multiple agents and multiple listings?',
      a: 'Yes. Upload all active listings and configure routing rules per agent or property type.',
    },
  ],
  ctaHeadline: 'Turn missed calls into captured leads.',
  ctaSub: 'Set up in under 10 minutes. Your AI receptionist handles the calls; your agents close the deals.',
};

export function RealEstateIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
